import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsColorOrReference } from '../../styles/validators/IsColorOrReference';
import { IsRgbaOrReference } from '../../styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
  InteractionEventDto,
  ShadowSettingsDto,
  SHADOW_TYPE_VALUES,
  type ShadowType,
} from './shared';
import { IsTRBLNumberCoords } from '../validators/IsTRBLNumberCoords';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
} from '@builder4/shared/types/animation';
import { GradientDto, RgbaDto } from '../../styles';

const COUNTDOWN_MODE_VALUES = ['standard', 'daily', 'evergreen'] as const;
const COUNTDOWN_TYPE_VALUES = ['default', 'short'] as const;
const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;

export class CountdownBreakpointPropsDto {
  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;
}

export class CountdownPropsDto {
  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @IsIn([...COUNTDOWN_MODE_VALUES])
  mode!: 'standard' | 'daily' | 'evergreen';

  // @IsISO8601() could be used here. Leaving as @IsString() for now since the original did so
  //  can tighten if confirmed safe down the road
  @IsDefined()
  @IsString()
  dateTime!: string;

  @IsOptional()
  @IsString()
  evergreenId?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  evergreenDuration?: number;

  @IsDefined()
  @IsIn([...COUNTDOWN_TYPE_VALUES])
  type!: 'default' | 'short';

  @IsDefined()
  @IsBoolean()
  showDays!: boolean;

  @IsDefined()
  @IsBoolean()
  showHours!: boolean;

  @IsDefined()
  @IsBoolean()
  showMinutes!: boolean;

  @IsDefined()
  @IsBoolean()
  showSeconds!: boolean;

  @IsDefined()
  @IsString()
  labelDays!: string;

  @IsDefined()
  @IsString()
  labelHours!: string;

  @IsDefined()
  @IsString()
  labelMinutes!: string;

  @IsDefined()
  @IsString()
  labelSeconds!: string;

  @IsDefined()
  @IsString()
  labelFontFamily!: string;

  @IsDefined()
  @IsString()
  labelFontWeight!: string;

  @IsDefined()
  @IsString()
  labelFontStyle!: string;

  @IsDefined()
  @IsString()
  labelFontSize!: string;

  @IsDefined()
  @IsString()
  numberFontFamily!: string;

  @IsDefined()
  @IsString()
  numberFontWeight!: string;

  @IsDefined()
  @IsString()
  numberFontStyle!: string;

  @IsDefined()
  @IsString()
  numberFontSize!: string;

  @IsOptional()
  @IsColorOrReference()
  backgroundColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  bgGradient?: GradientDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  bgSolid?: RgbaDto;

  @IsDefined()
  @IsColorOrReference()
  numberTextColor!: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  numberTextGradient?: GradientDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  numberTextSolid?: RgbaDto;

  @IsDefined()
  @IsColorOrReference()
  labelTextColor!: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  labelTextGradient?: GradientDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  labelTextSolid?: RgbaDto;

  @IsDefined()
  @IsNumber()
  @Min(0)
  borderWidth!: number;

  @IsOptional()
  @IsRgbaOrReference()
  borderColor?: RgbaDto | string;

  @IsDefined()
  @IsNumber()
  numberLetterSpacing!: number;

  @IsDefined()
  @IsNumber()
  labelLetterSpacing!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  gap!: number;

  @IsOptional()
  @IsIn([...SHADOW_TYPE_VALUES])
  fieldShadowType?: ShadowType;

  @IsOptional()
  @ValidateNested()
  @Type(() => ShadowSettingsDto)
  fieldShadowSettings?: ShadowSettingsDto;

  @IsDefined()
  @IsString()
  width!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minHeight!: number;

  @IsOptional()
  @IsIn(['yes', 'no'])
  fillSpace?: 'yes' | 'no';

  @IsOptional()
  @IsString()
  flexBasis?: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => InteractionEventDto)
  clickEvent!: InteractionEventDto;

  @IsDefined()
  @ValidateNested()
  @Type(() => InteractionEventDto)
  expireEvent!: InteractionEventDto;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;

  // Breakpoint overrides
  @IsOptional()
  @ValidateNested()
  @Type(() => CountdownBreakpointPropsDto)
  tablet?: CountdownBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => CountdownBreakpointPropsDto)
  mobile?: CountdownBreakpointPropsDto;
}
